import {
  EMPTY_PLACEHOLDER,
  formatCurrencyWithoutSign,
  removeDuplicates,
} from '@/common/utils/app.utils';
import { EmployeeDetailsByIdResponse } from '@/payroll-period/services/payrollPeriodApi';
import { SearchContributionsResponseType } from '@/employee-details/services/employeeDetailsApi';
import { GraphStatusesWithoutSubmitted } from '@/common/common.types';
import { EmployeeDetailsPageViewShape } from '@/components/withLinkToEmployeeDetails/withLinkToEmployeeDetails';
import { ERTNToggleValues } from '@/employee-details/components/PayrollERTNToggle/PayrollERTNToggle';
import tokens from '@/employee-details/tokens.json';

export function isValidArray(item: unknown) {
  return Array.isArray(item) && item.length > 0;
}

export function isValidEmployeeDetailsResponse(response: EmployeeDetailsByIdResponse) {
  return response && Object.keys(response).length > 0;
}

export function calculateContributionStatus(
  submitted: boolean,
  error: boolean,
  preSubmitError: boolean,
  preSubmitWarning: boolean,
  criticalError: boolean,
): GraphStatusesWithoutSubmitted {
  if (error === true || preSubmitError === true || criticalError === true) {
    return 'Errors';
  }

  if (preSubmitWarning === true) {
    return 'Warnings';
  }

  if (submitted === true) {
    return 'Accepted';
  }

  return 'Unsubmitted';
}

export function isNonTrueValue(value: unknown) {
  return value === null || value === undefined || String(value).trim() === '';
}

export function formatNumberForGridCell(value: number | string, type: 'currency' | 'fixed') {
  if (isNonTrueValue(value as string)) {
    return EMPTY_PLACEHOLDER;
  }

  const numeric = Number(value);
  if (isNaN(numeric)) {
    return EMPTY_PLACEHOLDER;
  }

  return type === 'fixed' ? numeric.toFixed(4) : formatCurrencyWithoutSign(numeric);
}

export function convertStatusToLabel(status: string) {
  if (status === 'D' || status === 'T') {
    return 'Inactive';
  }
  return 'Active';
}

export function covertLabelToTerminationStatus(label: string) {
  if (label === 'Inactive') {
    return 'T';
  } else if (label === 'Active') {
    return '';
  } else {
    return null;
  }
}

export function getEffectiveMonthRange(
  contributions: SearchContributionsResponseType[] | undefined,
): string {
  if (!contributions || contributions.length === 0) {
    return '';
  }

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const dates = contributions.map((contribution) => {
    return parseInt(contribution.effectiveDateofRecord, 10);
  });

  const minDate = Math.min(...dates);
  const maxDate = Math.max(...dates);

  const minMonth = parseInt(minDate.toString().substring(4, 6), 10);
  const maxMonth = parseInt(maxDate.toString().substring(4, 6), 10);

  const startMonthName = monthNames[minMonth - 1];
  const endMonthName = monthNames[maxMonth - 1];

  return `${startMonthName} to ${endMonthName}`;
}

export function getEmployeeIdsForValidation(data: SearchContributionsResponseType[]) {
  const employeeIds = data
    .filter(
      (contribution) =>
        contribution.submitted === false ||
        (contribution.submitted === true && contribution.error === true),
    )
    .map((contribution) => contribution.employeeId);

  if (employeeIds.length > 0) {
    return removeDuplicates(employeeIds);
  }

  return [];
}

export function isAddContributionDisabled(
  isLoadingContributions: boolean,
  lastKnownEmployeeId: number | undefined,
  isHasNoBatches: boolean,
  isLoadingBatchDetails: boolean,
  isErrorBatchDetails: boolean,
  isLoadingSearchEmployee: boolean,
  canAddContribution: boolean,
) {
  let isAddDisabled = false;
  let addDisabledReason = '';

  if (!canAddContribution) {
    isAddDisabled = true;
    addDisabledReason = 'User does not have permission';
  }
  if (isLoadingContributions) {
    isAddDisabled = true;
    addDisabledReason = 'Loading contributions';
  } else if (isLoadingBatchDetails) {
    isAddDisabled = true;
    addDisabledReason = 'Loading batches';
  } else if (isHasNoBatches) {
    isAddDisabled = true;
    addDisabledReason = 'Could not find batch';
  } else if (isErrorBatchDetails) {
    isAddDisabled = true;
    addDisabledReason = 'Could not load batches';
  } else if (isLoadingSearchEmployee) {
    isAddDisabled = true;
    addDisabledReason = 'Searching employees';
  } else if (lastKnownEmployeeId === undefined) {
    isAddDisabled = true;
    addDisabledReason = 'Could not find employee';
  }

  return {
    isAddDisabled,
    addDisabledReason,
  };
}

export function getSanitizedPageViewParam(param: string | null): EmployeeDetailsPageViewShape {
  if (param) {
    const typedParam = param as EmployeeDetailsPageViewShape;
    if (typedParam === 'ertn' || typedParam === 'payroll') {
      return typedParam;
    }
  }

  return 'payroll';
}

export function isToggleNotPayroll(toggleValue: ERTNToggleValues) {
  return toggleValue === 'ERTN' || toggleValue === 'ALL';
}

export function getContributionGridEmptyMessage(toggleValue: ERTNToggleValues, isLoading: boolean) {
  if (isLoading) {
    return '';
  }
  if (toggleValue === 'ERTN') {
    return tokens['NO_ERTN_CONTRIBUTION_FOUND'];
  }
  if (toggleValue === 'PAYROLL') {
    return tokens['NO_PAYROLL_CONTRIBUTION_FOUND'];
  }

  return tokens['NO_CONTRIBUTIONS_FOUND'];
}

export function isContributionViewOnly(
  isErtn: boolean,
  canEditContribution: boolean,
  isBatchAccepted: boolean,
) {
  return isErtn || !canEditContribution || isBatchAccepted;
}

export function isContributionChangeRecordDisabled(
  isErtn: boolean,
  canChangeRecord: boolean,
  isBatchAccepted: boolean,
  isContributionOverriden: boolean,
  isCRDisabled: boolean,
) {
  return isErtn || !canChangeRecord || !isBatchAccepted || isContributionOverriden || isCRDisabled;
}

export function isContributionCopyDisabled(
  isErtn: boolean,
  canCopyContribution: boolean,
  isCopyDisabled: boolean,
) {
  return isErtn || !canCopyContribution || isCopyDisabled;
}

export function isContributionDeleteDisabled(
  isErtn: boolean,
  canDeleteContribution: boolean,
  isBatchAccepted: boolean,
) {
  return isErtn || !canDeleteContribution || isBatchAccepted;
}

export function calculateContributionStatusForAddToErtn(
  submitted: boolean,
  error: boolean,
  preSubmitError: boolean,
  preSubmitWarning: boolean,
  criticalError: boolean,
): GraphStatusesWithoutSubmitted {
  if (error === true || preSubmitError === true || criticalError === true) {
    return 'Errors';
  }

  if (submitted === true) {
    return 'Accepted';
  }

  if (preSubmitWarning === true) {
    return 'Warnings';
  }

  return 'Unsubmitted';
}
